/**
 * WHITES FORESTRY CUSTOMISATION
 * WF-ID: WF-001
 * Purpose: Add the selected bar line and exactly one configured GA-003 line in
 * one Shopify Ajax Cart request when the customer explicitly opts in.
 * Reason: The Refresh 16.0.0 product form adds one line only. This isolated
 * multi-item path leaves assets/product-form.js and the unchecked path intact.
 * Pricing authority: Shopify automatic discount "Bar Nose Greaser Adapter".
 * No price is overridden or simulated here.
 */

if (!customElements.get('wf-bar-greaser-offer')) {
  customElements.define(
    'wf-bar-greaser-offer',
    class WfBarGreaserOffer extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.card = this.querySelector('[data-wf-offer-card]');
        this.checkbox = this.querySelector('[data-wf-offer-checkbox]');
        this.status = this.querySelector('[data-wf-offer-status]');
        this.accessoryVariantId = Number.parseInt(this.dataset.accessoryVariantId, 10);
        this.onSubmit = this.onSubmit.bind(this);

        const productInfo = document.querySelector(
          `product-info[data-product-id="${this.dataset.mainProductId}"][data-update-url="true"]`
        );
        this.productForm = productInfo?.querySelector('product-form');
        this.form = this.productForm?.querySelector('form[data-type="add-to-cart-form"]');

        if (!this.card || !this.checkbox || !this.status || !this.form || !this.accessoryVariantId) {
          return;
        }

        // Keep the Liquid section isolated while presenting the option beside
        // the purchase controls. The native Refresh section is not edited.
        this.productForm.before(this.card);
        this.card.hidden = false;
        this.form.addEventListener('submit', this.onSubmit, { capture: true });
      }

      disconnectedCallback() {
        this.form?.removeEventListener('submit', this.onSubmit, { capture: true });
        if (this.card && !this.contains(this.card)) this.append(this.card);
        if (this.card) this.card.hidden = true;
        this.initialized = false;
      }

      onSubmit(event) {
        if (!this.checkbox?.checked) return;

        event.preventDefault();

        const submitButton = this.productForm?.querySelector('[type="submit"]');
        if (!submitButton || this.isAdding || submitButton.getAttribute('aria-disabled') === 'true') return;

        /*
         * Set Refresh's existing guard before the event reaches its bubble-phase
         * product-form listener. The native listener therefore returns without a
         * second request; other event listeners can still observe the submission.
         */
        submitButton.setAttribute('aria-disabled', 'true');
        this.addSelectedOffer(event, submitButton);
      }

      async addSelectedOffer(event, submitButton) {
        this.isAdding = true;
        this.setStatus();
        this.productForm.handleErrorMessage?.();
        submitButton.classList.add('loading');
        this.productForm.querySelector('.loading__spinner')?.classList.remove('hidden');

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const formData = new FormData(this.form);
        let linesUpdateDeferred;

        try {
          const barItem = this.createBarItem(formData);
          const accessoryItem = { id: this.accessoryVariantId, quantity: 1 };
          linesUpdateDeferred = this.createCartLinesUpdateEvent([
            { merchandiseId: String(barItem.id), quantity: barItem.quantity },
            { merchandiseId: String(accessoryItem.id), quantity: 1 },
          ]);

          const requestBody = { items: [barItem, accessoryItem] };
          if (cart) {
            requestBody.sections = cart.getSectionsToRender().map((section) => section.id);
            requestBody.sections_url = window.location.pathname;
            cart.setActiveElement?.(document.activeElement);
          }

          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          config.body = JSON.stringify(requestBody);

          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', config);
          const cartData = await response.json();

          if (!response.ok || cartData.status) {
            throw new Error(cartData.description || cartData.message || 'The items could not be added to the cart.');
          }

          this.resolveCartLinesUpdate(linesUpdateDeferred);
          linesUpdateDeferred = null;
          this.checkbox.checked = false;
          this.setStatus('Added the bar and GA-003 adapter to your cart.');

          if (!cart) {
            window.location = window.routes?.cart_url || '/cart';
            return;
          }

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'wf-bar-greaser-offer',
            productVariantId: String(barItem.id),
            cartData,
          }).catch((publishError) => {
            // Match Refresh's native behavior: a subscriber failure must not
            // turn a completed Shopify cart mutation into a false add error.
            console.error('[WF-001] A cart-update subscriber failed.', publishError);
          });

          const renderState = this.createRenderState(cart, cartData, barItem.id);
          if (!renderState) {
            window.location = window.routes?.cart_url || '/cart';
            return;
          }

          try {
            cart.renderContents(renderState);
          } catch (renderError) {
            console.error('[WF-001] Cart UI refresh failed; opening the cart page.', renderError);
            window.location = window.routes?.cart_url || '/cart';
          }
        } catch (error) {
          console.error('[WF-001] Bar greaser add failed.', error);
          linesUpdateDeferred?.reject(error);
          this.publishCartError(error.message);
          this.dispatchCartErrorEvent(error.message);
          this.productForm.handleErrorMessage?.(error.message);
          this.setStatus(error.message, true);
        } finally {
          this.isAdding = false;
          submitButton.classList.remove('loading');
          // Match Refresh 16.0.0 ProductForm's successful-add cleanup. The
          // returned drawer section replaces #CartDrawer, but not its outer
          // <cart-drawer> host; leaving this class behind keeps the new cart in
          // the empty-state layout and focus path, most visibly on mobile.
          if (cart?.classList.contains('is-empty')) cart.classList.remove('is-empty');
          submitButton.removeAttribute('aria-disabled');
          this.productForm.querySelector('.loading__spinner')?.classList.add('hidden');

          if (typeof CartPerformance !== 'undefined') {
            CartPerformance.measureFromEvent('add:user-action', event);
          }
        }
      }

      createBarItem(formData) {
        const variantId = Number.parseInt(formData.get('id'), 10);
        const quantity = Number.parseInt(formData.get('quantity'), 10) || 1;

        if (!variantId || quantity < 1) {
          throw new Error('Please select an available bar option and quantity.');
        }

        const item = { id: variantId, quantity };
        const properties = {};

        for (const [key, value] of formData.entries()) {
          const match = key.match(/^properties\[(.+)]$/);
          if (!match || value === '') continue;
          if (typeof File !== 'undefined' && value instanceof File && value.size > 0) {
            throw new Error('File-based product options are not supported with this offer.');
          }
          properties[match[1]] = String(value);
        }

        if (Object.keys(properties).length) item.properties = properties;

        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) item.selling_plan = Number.parseInt(sellingPlan, 10) || sellingPlan;

        return item;
      }

      createRenderState(cart, cartData, barVariantId) {
        if (!cartData?.sections) return null;
        if (!cart.matches('cart-notification')) return cartData;

        const addedItems = Array.isArray(cartData.items) ? cartData.items : [];
        const barLine = addedItems.find((item) => String(item.id) === String(barVariantId));
        if (!barLine?.key) return null;

        return { ...barLine, sections: cartData.sections };
      }

      createCartLinesUpdateEvent(lines) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.productForm.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines,
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const cartUrl = window.routes?.cart_url || '/cart';
        fetch(`${cartUrl}.json`)
          .then((response) => response.json())
          .then((cart) => {
            if (!cart?.currency) throw new Error('Missing currency in cart response.');
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((error) => deferred.reject(error));
      }

      publishCartError(message) {
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'wf-bar-greaser-offer',
          productVariantId: String(this.accessoryVariantId),
          errors: message,
          message,
        });
      }

      dispatchCartErrorEvent(message) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.productForm.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
      }

      setStatus(message = '', isError = false) {
        this.status.textContent = message;
        this.status.dataset.error = isError ? 'true' : 'false';
      }
    }
  );
}
